// ============================================================
// TMR Portal Admin — Gestão de empresas (lista)
// ============================================================
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn, IconTile, StatusBadge } from '../../components/primitives';
import { PageHeader, SearchBox, FilterTabs, Table, Td } from '../../components/ui';
import { SelectField } from '../../components/fields';
import { Pagination } from '../../components/Pagination';
import { CopyableText } from '../../components/CopyableText';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { companyAddress, maskCNPJ } from '../../lib/format';
import {
  companyExportPage,
  useCompanyCounts,
  useCompanyList,
  COMPANY_PAGE_SIZE,
} from '../../data/companies/queries';
import { ExportMenu } from '../../components/ExportMenu';
import { COMPANY_EXPORT_COLUMNS } from './exportColumns';
import { gapSummary } from '../../data/companies/gaps';
import type { CompanyBusinessStatus, CompanyListItem } from '../../data/companies/contract';
import {
  BUSINESS_STATUS_BADGE,
  BUSINESS_STATUS_OPTIONS,
  SYNC_FILTERS,
  syncFilterQuery,
  syncState,
} from '../../data/companies/status';
import type { SyncFilterValue } from '../../data/companies/status';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useDebounced } from '../../useDebounced';
import { CompanyMapModal } from './CompanyMapModal';
import { ImportLegacyModal } from './ImportLegacyModal';
import { ManagerFilter, ALL_MANAGERS } from './ManagerFilter';
import type { ManagerFilterValue } from './ManagerFilter';
import { NewCompanyModal } from './NewCompanyModal';
import type { TmrSession } from '@tmr/auth';

/** The primary filter: every business status, or one of them. */
type BusinessStatusFilter = 'all' | CompanyBusinessStatus;

const TABS: { value: BusinessStatusFilter; label: string }[] = [
  { value: 'all', label: 'Todas' },
  ...BUSINESS_STATUS_OPTIONS.map((o) => ({ value: o.value as BusinessStatusFilter, label: o.label })),
];

/** Props for {@link CompaniesPage}. */
export interface CompaniesPageProps {
  /** The current operator — what the manager filters' "Eu" shortcut resolves to. */
  operator: TmrSession;
}

export function CompaniesPage({ operator }: CompaniesPageProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  // Two filters, in the order the operator thinks: where the Empresa stands as a business first, and only
  // then how it stands between the two systems.
  const [businessStatus, setBusinessStatus] = useState<BusinessStatusFilter>('all');
  const [sync, setSync] = useState<SyncFilterValue>('all');
  // After the legacy backfill this is the queue of work: the records that came across missing something
  // and cannot be replicated until somebody completes them (19 §5).
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  // Who manages the Empresa, per role. Filters, not scope: they narrow the page, never what the operator
  // may see (18 §9).
  const [salesManager, setSalesManager] = useState<ManagerFilterValue>(ALL_MANAGERS);
  const [postSalesManager, setPostSalesManager] = useState<ManagerFilterValue>(ALL_MANAGERS);
  const me: ManagerFilterValue = { userId: operator.userId, name: operator.name };
  const [page, setPage] = useState(1);
  const [mapCompany, setMapCompany] = useState<CompanyListItem | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const canOnboard = usePermission(PERMISSIONS.companyOnboard);
  const canBackfill = usePermission(PERMISSIONS.companyImportAdmin);

  const debouncedSearch = useDebounced(search);
  const counts = useCompanyCounts();

  // The filters, once — the table pages through them and the export walks all of them, so they cannot
  // be described twice and drift.
  const filters = {
    search: debouncedSearch || undefined,
    businessStatus: businessStatus === 'all' ? undefined : businessStatus,
    // "Importadas" and "Exportadas" are one export status told apart by origin, so the sub-filter yields
    // both parameters rather than one.
    ...syncFilterQuery(sync),
    salesManagerUserId: salesManager.userId || undefined,
    postSalesManagerUserId: postSalesManager.userId || undefined,
    registrationComplete: onlyIncomplete ? false : undefined,
  };

  const list = useCompanyList({ ...filters, page });

  const stats = [
    { label: 'Empresas cadastradas', value: counts.total },
    { label: 'Pendente contrato', value: counts.pendingContract },
    { label: 'Aguardando assinatura', value: counts.awaitingSignature },
    { label: 'Concluídas', value: counts.completed },
  ];

  const resetTo = (change: () => void) => {
    change();
    setPage(1);
  };

  const items = list.data?.items ?? [];
  const totalCount = list.data?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / (list.data?.pageSize ?? COMPANY_PAGE_SIZE)));

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Carteira"
        title="Empresas"
        desc="Estabelecimentos de saúde que usam a TMR como meio de pagamento."
        actions={
          <>
            {/* Exportar não é privilégio de quem cadastra: quem enxerga a lista pode levá-la embora, e o
                arquivo sai com exatamente as linhas que a tela já mostrava. */}
            <ExportMenu
              spec={{
                fileName: 'empresas',
                columns: COMPANY_EXPORT_COLUMNS,
                fetchPage: companyExportPage(filters),
              }}
              totalCount={totalCount}
              disabled={list.isPending}
            />
            {canBackfill && (
              <Btn variant="secondary" icon={Icons.download} onClick={() => navigate('/empresas/importar')}>
                Importar do legado
              </Btn>
            )}
            {canOnboard && (
              <>
                <Btn variant="secondary" icon={Icons.download} onClick={() => setShowImport(true)}>
                  Importar um produtor
                </Btn>
                <Btn variant="primary" icon={Icons.plus} onClick={() => setShowNew(true)}>
                  Nova empresa
                </Btn>
              </>
            )}
          </>
        }
      />

      <div className="pa-kpi-grid">
        {stats.map((s) => (
          <div key={s.label} className="pa-card" style={{ padding: 18 }}>
            <div className="pa-kpi-label" style={{ marginBottom: 8 }}>{s.label}</div>
            <div className="pa-num" style={{ fontSize: 26, lineHeight: 1 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* One filter row, not two. The sync filter used to be a second row of pills under its own label,
          which gave a secondary filter the same weight as the status tabs. As a dropdown beside the search
          it takes one line and reads as what it is: a narrowing, chosen when needed. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        <FilterTabs
          tabs={TABS}
          value={businessStatus}
          onChange={(v) => resetTo(() => setBusinessStatus(v as BusinessStatusFilter))}
        />
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ width: 190 }}>
            <SelectField
              label="Sincronia"
              value={sync === 'all' ? '' : sync}
              // The empty option is the placeholder, and "todas" is exactly what it means here.
              onChange={(v) => resetTo(() => setSync((v || 'all') as SyncFilterValue))}
              options={SYNC_FILTERS.filter((f) => f.value !== 'all')}
              placeholder="Todas"
            />
          </div>
          <SearchBox
            value={search}
            onChange={(v) => resetTo(() => setSearch(v))}
            placeholder="Buscar empresa, CNPJ, domínio…"
            width={320}
          />
        </div>
      </div>

      {/* Their own row rather than crowded in beside the search: these two are how a colleague isolates
          their carteira, which is a different question from "where does this Empresa stand". */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
        <ManagerFilter
          label="Gestor de venda"
          subtitle="Mostra apenas as empresas vendidas por esta pessoa."
          value={salesManager}
          onChange={(v) => resetTo(() => setSalesManager(v))}
          me={me}
        />
        <ManagerFilter
          label="Gestor de pós-venda"
          subtitle="Mostra apenas as empresas que esta pessoa acompanha após a venda."
          value={postSalesManager}
          onChange={(v) => resetTo(() => setPostSalesManager(v))}
          me={me}
        />

        {/* A toggle, not a third dropdown: after the backfill this is a queue somebody works through, and
            a queue is something you switch on, not a value you pick. */}
        <div>
          <label className="pa-form-label">Cadastro</label>
          <Btn
            variant={onlyIncomplete ? 'primary' : 'secondary'}
            onClick={() => resetTo(() => setOnlyIncomplete((v) => !v))}
            title="Empresas que vieram do legado sem CNPJ, sem representante ou com endereço incompleto."
          >
            <StrokeIcon d={Icons.alert} size={15} /> Incompletos
          </Btn>
        </div>
      </div>

      <CompanyTable
        list={list}
        items={items}
        search={debouncedSearch}
        managerFiltered={Boolean(salesManager.userId || postSalesManager.userId)}
        onOpen={(id) => navigate(`/empresas/${id}`)}
        onOpenMap={setMapCompany}
      />

      {items.length > 0 && (
        <Pagination page={page} pageCount={pageCount} total={totalCount} onChange={setPage} />
      )}

      {showNew && (
        <NewCompanyModal
          onClose={() => setShowNew(false)}
          // Straight to the record: the export to the legacy is still running, and its indicator lives there.
          // `replace` takes the wizard's own history entry with it, so Back from the record returns to this
          // list rather than to a copy of it.
          onCreated={(companyId) => navigate(`/empresas/${companyId}`, { replace: true })}
        />
      )}

      {showImport && (
        <ImportLegacyModal
          onClose={() => setShowImport(false)}
          onImported={(companyId) => navigate(`/empresas/${companyId}`)}
        />
      )}

      {mapCompany && (
        <CompanyMapModal
          name={mapCompany.name}
          address={mapCompany.address}
          onClose={() => setMapCompany(null)}
        />
      )}
    </div>
  );
}

/** Props for {@link CompanyTable}. */
interface CompanyTableProps {
  list: ReturnType<typeof useCompanyList>;
  items: CompanyListItem[];
  search: string;
  /** Whether either manager filter is narrowing the page — it changes what "vazio" means. */
  managerFiltered: boolean;
  onOpen: (companyId: string) => void;
  onOpenMap: (company: CompanyListItem) => void;
}

/** The table and the three states it can be in besides "has rows". */
function CompanyTable({ list, items, search, managerFiltered, onOpen, onOpenMap }: CompanyTableProps) {
  // A first load has nothing to show yet; a refetch keeps the previous page under the new query.
  if (list.isPending) return <Skeleton rows={6} />;

  // Includes the missing-permission case: ErrorState words a 403 as refusal, without a retry button.
  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;

  if (items.length === 0) {
    // A carteira que está vazia por causa do filtro de gestor é o caso mais comum de "não achei nada"
    // agora, e é o único em que a dica útil não é sobre a busca.
    if (managerFiltered && !search) {
      return (
        <EmptyState
          message="Nenhuma empresa para este gestor."
          hint="Limpe o filtro de gestor para ver a carteira inteira."
        />
      );
    }

    return search ? (
      <EmptyState
        message="Nenhuma empresa encontrada."
        hint="Revise o termo buscado, o status da empresa ou os filtros de gestor e sincronia."
      />
    ) : (
      <EmptyState
        message="Nenhuma empresa cadastrada ainda."
        hint="Cadastre uma nova empresa ou importe um produtor que já existe no sistema legado."
      />
    );
  }

  return (
    <Table
      columns={[
        { label: 'Empresa' },
        { label: 'CNPJ' },
        { label: 'Domínio' },
        { label: 'Gestores' },
        { label: 'Sincronia' },
        { label: '', align: 'right', width: 44 },
      ]}
    >
      {items.map((company) => (
        <tr
          key={company.id}
          className="pa-row"
          onClick={() => onOpen(company.id)}
          style={{ cursor: 'pointer' }}
        >
          <Td>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {companyAddress(company.address).precision !== 'none' ? (
                <button
                  type="button"
                  className="pa-map-tile"
                  title="Ver no mapa"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenMap(company);
                  }}
                >
                  <IconTile size={38}>
                    <StrokeIcon d={Icons.clinic} size={18} />
                  </IconTile>
                </button>
              ) : (
                <IconTile size={38}>
                  <StrokeIcon d={Icons.clinic} size={18} />
                </IconTile>
              )}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 500 }}>{company.name}</span>
                  <StatusBadge status={BUSINESS_STATUS_BADGE[company.status]} />
                  {/* The badge says *what* is missing, not just that something is: "cadastro incompleto"
                      alone sends the operator into the record to find out, which is the trip the badge
                      exists to save. */}
                  {company.registrationGaps.length > 0 && (
                    <span
                      title={`Falta: ${gapSummary(company.registrationGaps)}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '2px 7px',
                        borderRadius: 'var(--radius-xs)',
                        fontSize: 11.5,
                        background: 'oklch(96% 0.04 80)',
                        color: 'oklch(38% 0.10 60)',
                      }}
                    >
                      <StrokeIcon d={Icons.alert} size={12} /> {gapSummary(company.registrationGaps)}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>
                  {[company.address.city, company.address.state].filter(Boolean).join('/') || '—'}
                </div>
              </div>
            </div>
          </Td>
          <Td>
            {company.cnpj ? (
              <CopyableText value={maskCNPJ(company.cnpj)} style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} />
            ) : (
              <span style={{ color: 'var(--fg-subtle)', fontSize: 13 }}>—</span>
            )}
          </Td>
          <Td>
            <CopyableText value={company.subdomain} style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} />
          </Td>
          <Td>
            {/* Venda em cima, pós-venda embaixo, sempre nessa ordem e sempre as duas linhas: um par que
                muda de lugar conforme o que está preenchido é impossível de ler na vertical. */}
            <div style={{ fontSize: 13 }}>
              <div>{company.salesManager?.name || '—'}</div>
              <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>
                {company.postSalesManager?.name || '—'}
              </div>
            </div>
          </Td>
          <Td>
            <StatusBadge status={syncState(company.exportStatus, company.recordOrigin)} />
          </Td>
          <Td align="right">
            <span style={{ color: 'var(--stone-400)', display: 'inline-flex' }}>
              <StrokeIcon d={Icons.chevronR} size={16} />
            </span>
          </Td>
        </tr>
      ))}
    </Table>
  );
}
